import { Component, HostBinding, OnInit, OnDestroy, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe, DOCUMENT } from '@angular/common';

import { Observable, Subscription, tap, TeardownLogic } from 'rxjs';

import { ConfirmationDialogService, MessageBoxDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs';
import { ScreenSizeService } from 'services/screensize/screensize.service';

import { AuthService } from 'services/authentication/auth.service';
import { BingMapsService } from 'modules/shared/ui/bingmap/bingmap.service';
import { UnsavedChangesService } from 'routes/routes.guard';
import { ThemeService, ThemeType } from 'services/theme/theme.service';
import { UserService } from 'services/user/user.service';
import { UserInfo } from 'services/user/user.service.class';
import { BUILD_NOTES, BUILD_TIME, VERSION_NUMBER } from 'services/version/version.service';
import { RoutePaths } from 'routes/route.roles';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    @HostBinding('class.smalldisplay') SmallDisplay: boolean;

    private _subrelease: Subscription = new Subscription();
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }

    AboutFn: () => Observable<boolean>;
    LogoutFn: () => Observable<boolean>;

    BuildNotes: string;
    BuildTime: string;
    CurrentRole: string;
    CurrentTheme: ThemeType | null = null;
    DevSite: boolean;
    LastSizeCode: string;
    LastSmallDisplay: boolean;
    SizeCode: string;
    UpdateAvailable: boolean;
    User: UserInfo | null = null;
    VersionLoaded: string;
    ViewReady: boolean;

    constructor(
        private authSvc: AuthService,
        private bingMapsSvc: BingMapsService,
        private confDlgSvc: ConfirmationDialogService,
        private datePipe: DatePipe,
        @Inject(DOCUMENT) private document: Document,
        private screenSizeSvc: ScreenSizeService,
        private msgBoxDlgSvc: MessageBoxDialogService,
        private router: Router,
        private themeSvc: ThemeService,
        private unsavedChanges: UnsavedChangesService,
        private userSvc: UserService
    ) {
        this.AboutFn = this.About.bind(this);
        this.BuildNotes = BUILD_NOTES;
        this.CurrentRole = '';
        this.DevSite = false;
        this.LastSizeCode = '';
        this.LogoutFn = this.Logout.bind(this);
        this.SizeCode = '';
        this.SmallDisplay = false;
        this.LastSmallDisplay = false;
        this.UpdateAvailable = false;
        this.VersionLoaded = VERSION_NUMBER;
        this.ViewReady = false;

        const btime = (BUILD_TIME && new Date(BUILD_TIME)) || new Date();
        this.BuildTime = (btime && this.datePipe.transform(btime, 'MMM. dd, yyyy h:mm a')) || '';

        // Subscribe to media changes
        this.subrelease = this.screenSizeSvc.ScreenSizeSub.subscribe((size) => {
            this.SmallDisplay = size.IsSmallDisplay;
            this.SizeCode = size.Alias;

            this.LastSmallDisplay = this.SmallDisplay;
        });

        // Monitor user subject for updates.
        this.subrelease = this.userSvc.UserSubject.subscribe((_u) => {
            this.User = _u;

            this.ViewReady = true;
        });

        // Subscribe to app theme changes.
        this.subrelease = this.themeSvc.CurrentThemeSub.subscribe((theme) => {
            if (theme != null) {
                this.CurrentTheme = theme;
                this.document.body.classList.remove(theme === 'my-light-theme' ? 'my-dark-theme' : 'my-light-theme');
                this.document.body.classList.add(theme);
            }
        });
    }

    ngOnDestroy(): void {
        this._subrelease.unsubscribe();
    }

    ngOnInit(): void {
        const self = this;
    }

    // #region About Method

    About(): Observable<boolean> {
        const self = this;

        let msg =
            `The Principals Profiles system is a product of the Polk County Public Schools system. The system provides an easy way for school principals to share some background information on themselves and their school to district leadership and school board members for easy reference.<br /><br />` +
            `<strong>Profiles Version: </strong>${self.VersionLoaded}<br />
            <strong>Build Date/Time: </strong>${self.BuildTime}`;

        if (self.BuildNotes) {
            msg += `<br /><strong>Build Notes: </strong><br />` + self.BuildNotes;
        }

        return self.msgBoxDlgSvc.Open({
            TitleText: 'About Principals Profiles',
            MessageText: msg,
        });
    }

    // #endregion

    // #region Go Home Method

    GoHome(): void {
        const self = this;

        if (!self.User || !self.User.AuthToken) {
            self.router.navigate([`/${RoutePaths.Login}`]);
        } else {
            self.router.navigate([`/${RoutePaths.Home}`]);
        }
    }

    // #endregion

    // #region Logout Method

    Logout(): Observable<boolean> {
        const self = this;

        // If we don't have a tracker in place, and we're on xs size code, we need to ensure a dialog will open.
        let confObs: Observable<boolean> | null = null;

        if (!self.unsavedChanges.HasTracker() && self.SizeCode === 'xs') {
            confObs = null; // Ensures it will defer to dialog.
        } else {
            confObs = self.unsavedChanges.CheckTracker?.(); // This will attempt to use a check tracker.
        }

        return (
            confObs ||
            // self.dialogService.OpenConfirmationDialog({
            self.confDlgSvc.Open({
                TitleText: 'Confirmation Requested',
                MessageText:
                    'You are about to log out of the PCPS Principals Profiles system. Are you sure you wish to exit at this time?',
                OKText: 'Yes - Log Out',
                CancelText: 'No - Stay Logged In',
            })
        ).pipe(
            tap((result) => {
                if (result === true) {
                    // The tracker says it's okay to logout, Go ahead and logout without further confirmation.
                    // Disable change tracking first.
                    self.unsavedChanges.ClearTracking();

                    // Now perform the logout.
                    self.authSvc.Logout();
                }
            })
        );
    }

    // #endregion

    // #region Toggle Theme

    ToggleTheme(): void {
        this.themeSvc.SetTheme(this.CurrentTheme === 'my-light-theme' ? 'my-dark-theme' : 'my-light-theme');
    }

    // #endregion

    // #region Toolbar About Method

    ToolbarAbout(): void {
        this.About().subscribe();
    }

    // #endregion

    // #region Toolbar Logout Method

    ToolbarLogout(): void {
        this.Logout().subscribe();
    }

    // #endregion
}
